(() => {
  const configNode = document.getElementById('hh-discount-config');
  if (!configNode || window.hhDiscountsLoaded) return;
  window.hhDiscountsLoaded = true;
  const config = JSON.parse(configNode.textContent);
  const root = config.root;
  const texts = config.texts;
  const storageKey = 'hh-discount-pending-v1';
  let cart = null;
  let pending = [];
  let active = [];
  let entered = [];
  let busy = false;
  let initializing = true;
  let lastMessage = '';
  let failed = false;
  let serial = Promise.resolve();
  const normalize = value => String(value || '').trim().toUpperCase();
  const unique = values => [...new Set(values.map(normalize).filter(Boolean))];
  const message = (key, values = {}) => Object.entries(values).reduce((s, [k, v]) => s.replaceAll(`[[${k}]]`, v), texts[key] || '');
  const queue = task => {
    serial = serial.catch(() => {}).then(task);
    return serial;
  };
  function savePending() {
    try {
      if (pending.length) sessionStorage.setItem(storageKey, JSON.stringify({ codes: pending, expires: Date.now() + 86400000 }));
      else sessionStorage.removeItem(storageKey);
    } catch (_) {}
  }
  try {
    const saved = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
    if (saved?.expires > Date.now() && Array.isArray(saved.codes)) pending = unique(saved.codes);
  } catch (_) {}
  function applications(value) {
    return [
      ...(value.cart_level_discount_applications || []),
      ...(value.items || []).flatMap(item => (item.line_level_discount_allocations || []).map(allocation => allocation.discount_application))
    ].filter(Boolean);
  }
  function ingest(value, requestedCodes = null) {
    cart = value;
    const apps = applications(value);
    active = unique([
      ...(value.discount_codes || []).filter(code => code.applicable === true).map(code => code.code),
      ...apps.filter(app => app.type === 'discount_code').map(app => app.title)
    ]);
    const allReturned = (value.discount_codes || []).map(code => code.code);
    // A fresh cart response wins over saved or in-memory codes. A submitted
    // code is retained only to explain an unsuccessful application to the user.
    if (requestedCodes !== null) pending = unique(requestedCodes);
    else if (Array.isArray(value.discount_codes) || active.length) pending = unique([...allReturned, ...active]);
    entered = unique([...allReturned, ...active, ...pending]);
    pending = unique([...pending, ...(value.item_count === 0 ? allReturned : [])]).filter(code => !active.includes(code));
    savePending();
  }
  async function request(path, body) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(root + path, {
        method: body ? 'POST' : 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        ...(body ? { body: JSON.stringify(body) } : {}),
        signal: controller.signal
      });
      if (!response.ok) throw new Error('Cart request failed');
      return await response.json();
    } finally { clearTimeout(timeout); }
  }
  function setBusy(value) {
    busy = value;
    document.querySelectorAll('[data-hh-discount-form]').forEach(form => {
      form.setAttribute('aria-busy', String(value));
      form.querySelectorAll('button').forEach(button => { button.disabled = value; });
    });
    document.querySelectorAll('hh-discount-codes button').forEach(button => { button.disabled = value; });
  }
  function updateCheckoutFields() {
    document.querySelectorAll('[data-hh-discount-form]').forEach(component => {
      const form = component.closest('form');
      if (!form) return;
      let hidden = form.querySelector('[data-hh-discount-checkout]');
      const codes = unique([...active, ...pending, ...entered]);
      if (!codes.length) { hidden?.remove(); return; }
      if (!hidden) {
        hidden = document.createElement('input');
        hidden.type = 'hidden'; hidden.name = 'discount';
        hidden.setAttribute('data-hh-discount-checkout', '');
        form.append(hidden);
      }
      hidden.value = codes.join(',');
    });
  }
  function statusText() {
    if (failed) return message('network');
    if (active.length) return message('active', { code: active.join(', ') });
    if (entered.length) return message(cart?.item_count ? 'not_applicable' : 'reserved', { code: entered.join(', ') });
    return '';
  }
  function render() {
    document.querySelectorAll('[data-hh-discount-message]').forEach(node => {
      node.textContent = lastMessage || statusText();
      node.hidden = !failed && active.length > 0 && node.textContent === message('active', { code: active.join(', ') });
    });
    document.querySelectorAll('[data-hh-discount-form]').forEach(form => {
      const currentCodes = unique([...entered, ...active]);
      form.querySelectorAll('[data-hh-active-code]').forEach(node => {
        if (!currentCodes.includes(normalize(node.dataset.hhActiveCode))) node.remove();
      });
      let list = form.querySelector('[data-hh-discount-pending-list]');
      if (!list) {
        list = document.createElement('div');
        list.className = 'hh-discount-codes';
        list.setAttribute('data-hh-discount-pending-list', '');
        form.append(list);
      }
      list.replaceChildren();
      const serverCodes = unique([...form.querySelectorAll('[data-hh-active-code]')].map(node => node.dataset.hhActiveCode));
      unique([...entered, ...active]).filter(code => !serverCodes.includes(code)).forEach(code => {
        const button = createCodeTag(code, true);
        list.append(button);
      });
    });
    updateCheckoutFields();
    renderCodes();
    setBusy(busy);
    window.hhDiscountState = { codes: unique([...active, ...entered]), active: [...active], cart, failed, initializing, busy };
    window.dispatchEvent(new CustomEvent('hh:discount:state', { detail: window.hhDiscountState }));
  }
  function createCodeTag(code, removable) {
    const tag = document.createElement(removable ? 'button' : 'span');
    tag.className = 'hh-discount-tag';
    if (removable) {
      tag.type = 'button';
      tag.dataset.hhDiscountRemove = code;
      tag.setAttribute('aria-label', message('remove_code', { code }));
    }
    const state = failed ? 'unverified' : active.includes(code) ? 'applied' : cart?.item_count ? 'unavailable' : 'saved';
    tag.dataset.discountState = state;
    tag.title = message(`badge_${state}`, { code });
    const icon = document.createElement('span');
    icon.className = 'hh-discount-tag__icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.innerHTML = '<svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11 2H5a3 3 0 0 0-3 3v6l6 6a2 2 0 0 0 3 0l6-6a2 2 0 0 0 0-3l-6-6Z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/><circle cx="6" cy="6" r="1.3" fill="currentColor"/></svg>';
    const label = document.createElement('span');
    label.className = 'hh-discount-tag__label';
    label.textContent = code;
    tag.append(icon, label);
    if (removable) {
      const close = document.createElement('span');
      close.className = 'hh-discount-tag__close';
      close.setAttribute('aria-hidden', 'true');
      close.textContent = '×';
      tag.append(close);
    }
    return tag;
  }
  function renderCodes() {
    const codes = unique([...active, ...entered]);
    document.querySelectorAll('hh-discount-codes').forEach(node => {
      const signature = JSON.stringify([codes, active, failed, cart?.item_count || 0]);
      if (node.dataset.renderedCodes !== signature) {
        node.replaceChildren(...codes.map(code => createCodeTag(code, node.dataset.scope === 'product')));
        node.dataset.renderedCodes = signature;
      }
      node.hidden = !codes.length;
    });
  }
  function clearLinkCode() {
    const url = new URL(location.href);
    if (!url.searchParams.has('discount') && !url.searchParams.has('hh_discount')) return;
    url.searchParams.delete('discount'); url.searchParams.delete('hh_discount');
    history.replaceState(history.state, '', url.href);
  }
  async function apply(codes, submitted = '') {
    setBusy(true); failed = false;
    lastMessage = message('checking'); render();
    try {
      const result = await request('cart/update.js', { discount: unique(codes).join(',') });
      ingest(result, codes);
      clearLinkCode();
      if (!codes.length) lastMessage = message('removed');
      else if (submitted && !active.includes(normalize(submitted))) lastMessage = message(result.item_count ? 'not_applicable' : 'reserved', { code: submitted });
      else lastMessage = statusText();
      window.dispatchEvent(new CustomEvent('cart:changed', { detail: { cart: result, hhDiscountHandled: true } }));
      return true;
    } catch (_) {
      failed = true; lastMessage = message('network');
      return false;
    } finally { setBusy(false); render(); }
  }
  document.addEventListener('click', event => {
    const applyButton = event.target.closest('[data-hh-discount-apply]');
    const removeButton = event.target.closest('[data-hh-discount-remove]');
    if (!applyButton && !removeButton) return;
    event.preventDefault();
    if (busy) return;
    if (removeButton) {
      const remove = normalize(removeButton.dataset.hhDiscountRemove);
      queue(() => apply(unique([...entered, ...active, ...pending]).filter(code => code !== remove)));
    } else {
      const input = applyButton.closest('[data-hh-discount-form]').querySelector('[data-hh-discount-input]');
      const code = normalize(input.value);
      if (!code) { lastMessage = message('empty'); render(); input.focus(); return; }
      queue(async () => {
        const okay = await apply([code], code);
        if (okay) input.value = '';
      });
    }
  });
  document.addEventListener('keydown', event => {
    if (event.key === 'Enter' && event.target.matches('[data-hh-discount-input]')) {
      event.preventDefault();
      event.target.closest('[data-hh-discount-form]').querySelector('[data-hh-discount-apply]').click();
    }
  });
  const resuming = new WeakSet();
  const checkoutQueued = new WeakSet();
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!form.querySelector('[data-hh-discount-form]') || resuming.has(form)) return;
    const input = form.querySelector('[data-hh-discount-input]');
    const code = normalize(input?.value);
    if (!code && !busy && !initializing) return;
    event.preventDefault();
    if (checkoutQueued.has(form)) return;
    checkoutQueued.add(form);
    const submitter = event.submitter;
    queue(async () => {
      if (code && !await apply([code], code)) { checkoutQueued.delete(form); return; }
      updateCheckoutFields();
      if (input) input.value = '';
      resuming.add(form);
      const currentSubmitter = submitter?.isConnected ? submitter : form.querySelector('[name="checkout"]');
      form.requestSubmit(currentSubmitter || undefined);
      resuming.delete(form);
      checkoutQueued.delete(form);
    });
  }, true);
  window.addEventListener('hh:cart:rendered', render);
  window.addEventListener('cart:changed', event => {
    if (event.detail?.hhDiscountHandled) return;
    queue(async () => {
      try { ingest(await request('cart.js')); failed = false; lastMessage = ''; render(); }
      catch (_) { failed = true; render(); }
    });
  });
  window.addEventListener('cart:add', () => {
    queue(async () => {
      if (pending.length) return apply(unique([...active, ...entered, ...pending]));
      try { ingest(await request('cart.js')); failed = false; lastMessage = ''; render(); }
      catch (_) { failed = true; render(); }
    });
  });
  window.addEventListener('hh:selling-plan:change', render);
  document.addEventListener('shopify:section:load', render);
  window.addEventListener('pageshow', event => {
    if (event.persisted) queue(async () => {
      try { ingest(await request('cart.js')); failed = false; lastMessage = ''; render(); }
      catch (_) { failed = true; render(); }
    });
  });
  let codeFrame;
  new MutationObserver(records => {
    if (!records.some(record => [...record.addedNodes].some(node => node.nodeType === 1 && (node.matches('hh-discount-codes') || node.querySelector('hh-discount-codes'))))) return;
    cancelAnimationFrame(codeFrame);
    codeFrame = requestAnimationFrame(render);
  }).observe(document.querySelector('main') || document.body, { childList: true, subtree: true });
  const url = new URL(location.href);
  const incoming = url.searchParams.get('discount') || url.searchParams.get('hh_discount');
  const incomingCodes = incoming ? unique(incoming.split(',')) : [];
  if (incomingCodes.length) pending = incomingCodes;
  entered = unique(pending);
  savePending();
  queue(async () => {
    try {
      const initialCart = await request('cart.js');
      ingest(initialCart);
      const serverCodes = unique([...(initialCart.discount_codes || []).map(code => code.code), ...active]);
      if (incomingCodes.length && (incomingCodes.length !== serverCodes.length || incomingCodes.some(code => !serverCodes.includes(code)))) await apply(incomingCodes, incomingCodes.join(', '));
      else if (pending.some(code => !serverCodes.includes(code))) await apply(unique([...active, ...entered, ...pending]));
      else { clearLinkCode(); render(); }
    } catch (_) { failed = true; render(); }
    finally { initializing = false; render(); }
  });
})();
